import { NoteBlock, NoteBlockType } from '../../types';
import { editorNodeText, EditorNode, markdownToEditor } from './markdownToEditor';

export interface ImportedSecret {
  blockId: string;
  label: string;
  value: string;
}

export interface MarkdownImportResult {
  title: string;
  tags: string[];
  blocks: NoteBlock[];
  secrets: ImportedSecret[];
}

export interface MarkdownParseOptions {
  preserveFirstHeading?: boolean;
}

export const MAX_MARKDOWN_FILE_BYTES = 1024 * 1024;

export function looksLikeMarkdown(value: string) {
  return /(^|\n)\s{0,3}(?:#{1,6}\s+|```|~~~|:::secret\b|>\s+|- \[[ xX]\]\s+|[-+*]\s+|\d+[.)]\s+|\[\^[^\]]+\]:|(?:---+|___+|\*\*\*+)\s*$)/m.test(value)
    || /(?:^|\n)[^\n]+\n(?:=+|-+)\s*(?:\n|$)/m.test(value)
    || /(?:^|\n)\s*\|?.+\|.+\|?\s*\n\s*\|?\s*:?-{3,}:?\s*\|/m.test(value)
    || /\*\*[^*\n]+\*\*|(?:^|\s)\*[^*\n]+\*(?=$|[\s).,!?:;])|`[^`\n]+`|~~[^~\n]+~~|\+\+[^+\n]+\+\+|==[^=\n]+==|!\[[^\]]*\]\([^)]+\)|\[@[^\]]+\]|\[\^[^\]]+\]|\$[^$\n]+\$|\[[^\]\n]+\]\([^)]+\)/.test(value);
}

export function markdownImportCreateInput(result: MarkdownImportResult) {
  return {
    title: result.title,
    tags: result.tags,
    content: result.blocks,
    secrets: result.secrets,
  };
}

const IMPORT_TEMPLATE = `---
title: Ourora Notes Markdown Template
tags: [template, markdown, notes]
---

# Heading 1

Use **bold**, *italic*, ~~strikethrough~~, ++underline++, ==highlight==, and \`inline code\`.

#### Headings 4 through 6 work too

- Parent list item
  - Nested list item
    1. Nested numbered item

### Tasks

- [ ] Open task
- [x] Completed task

| Feature | Status | Count |
| :--- | :---: | ---: |
| Markdown import | **Ready** | 3 |
| Escaped pipe | A \\| B | 2 |

Term
: A definition attached to the term.

> Quotes can contain nested blocks.
>
> - Including lists

[Reference link][ourora], <hello@example.com>, and citation [@ourora-notes, section 2].

[ourora]: https://ourora.co "Ourora"

![Remote image](https://placehold.co/720x240/241f22/eadde5?text=Ourora+Notes "Sample image")

Footnote reference[^details]. Inline math: $E = mc^2$.

[^details]: Footnotes render at the bottom of the note.

$$
\\int_0^1 x^2\\,dx = \\frac{1}{3}
$$

\`\`\`mermaid
flowchart LR
  Markdown --> Notes
  Notes --> Collaboration
\`\`\`

~~~typescript
const greeting = 'Tilde and backtick fences both work';
~~~

Raw HTML is displayed safely as source code:

<section class="example">
  <strong>This markup is shown, not executed.</strong>
</section>

:::secret label="Example API token"
replace-with-a-value
:::

## Import rules

- Save as UTF-8 with a .md or .markdown extension. Files may be up to 1 MiB.
- Front matter is optional and supports title plus comma-separated or array-style tags.
- Without a front-matter title, the first Heading 1 becomes the note title.
- Remote images render. Relative image paths require their files to be hosted separately.
- Raw HTML is shown in an HTML code block and is never executed.
- Secret directive contents are uploaded as protected values. Do not retain real secrets in plaintext files.
`;

export const markdownImportTemplate = IMPORT_TEMPLATE;

const createId = () => typeof crypto !== 'undefined' && crypto.randomUUID
  ? crypto.randomUUID()
  : `import-${Date.now()}-${Math.random().toString(16).slice(2)}`;

function parseFrontMatter(markdown: string) {
  const lines = markdown.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n').split('\n');
  if (lines[0]?.trim() !== '---') return { lines, title: '', tags: [] as string[] };
  const end = lines.slice(1).findIndex(line => line.trim() === '---');
  if (end < 0) return { lines, title: '', tags: [] as string[] };
  const metadata = lines.slice(1, end + 1);
  const titleLine = metadata.find(line => /^title\s*:/i.test(line));
  const tagsLine = metadata.find(line => /^tags\s*:/i.test(line));
  const clean = (value: string) => value.trim().replace(/^['"]|['"]$/g, '');
  const title = titleLine ? clean(titleLine.replace(/^title\s*:/i, '')) : '';
  const rawTags = tagsLine ? tagsLine.replace(/^tags\s*:/i, '').trim().replace(/^\[|\]$/g, '') : '';
  const tags = rawTags.split(',').map(clean).filter(Boolean).slice(0, 20);
  return { lines: lines.slice(end + 2), title, tags };
}

function noteBlock(type: NoteBlockType, text: string, editorNode: EditorNode, extra: Partial<NoteBlock> = {}): NoteBlock {
  return { id: createId(), type, text, editorNode: editorNode as Record<string, unknown>, ...extra };
}

function fallbackTitle(fileName: string) {
  return fileName.replace(/\.(md|markdown)$/i, '').replace(/[-_]+/g, ' ').trim() || 'Imported note';
}

export function parseMarkdownNote(markdown: string, fileName: string, options: MarkdownParseOptions = {}): MarkdownImportResult {
  const frontMatter = parseFrontMatter(markdown);
  const secrets: ImportedSecret[] = [];
  const sourceLines: string[] = [];
  let title = frontMatter.title;
  for (let index = 0; index < frontMatter.lines.length; index += 1) {
    const secretStart = frontMatter.lines[index].match(/^\s*:::secret(?:\s+label=(?:"([^"]*)"|'([^']*)'|([^\s]+)))?\s*$/i);
    if (!secretStart) { sourceLines.push(frontMatter.lines[index]); continue; }
    const label = secretStart[1] || secretStart[2] || secretStart[3] || 'Protected value';
    const body: string[] = [];
    index += 1;
    while (index < frontMatter.lines.length && !/^\s*:::\s*$/.test(frontMatter.lines[index])) body.push(frontMatter.lines[index++]);
    const blockId = createId();
    const value = body.join('\n').trim();
    const secretIndex = secrets.length;
    secrets.push({ blockId, label, value });
    sourceLines.push('', `<!--ourora-secret:${secretIndex}-->`, '');
  }

  let nodes = markdownToEditor(sourceLines.join('\n')).map(node => {
    if (node.type !== 'secretPlaceholder') return node;
    const secret = secrets[Number(node.attrs?.index)];
    return { type: 'secretBlock', attrs: { blockId: secret.blockId, label: secret.label, hasValue: Boolean(secret.value) } } as EditorNode;
  });
  const titleIndex = nodes.findIndex(node => node.type === 'heading' && node.attrs?.level === 1);
  if (!title && titleIndex >= 0 && !options.preserveFirstHeading) {
    title = editorNodeText(nodes[titleIndex]).trim();
    nodes = nodes.filter((_, index) => index !== titleIndex);
  }
  const blockType = (node: EditorNode): NoteBlockType => node.type === 'heading' ? 'heading'
    : node.type === 'codeBlock' ? 'code'
      : node.type === 'taskList' ? 'checklist'
        : node.type === 'secretBlock' ? 'secret'
          : 'paragraph';
  const blocks = nodes.map(node => noteBlock(blockType(node), editorNodeText(node), node, node.type === 'codeBlock'
    ? { language: String(node.attrs?.language || '') || undefined }
    : {}));

  return { title: (title || fallbackTitle(fileName)).slice(0, 180), tags: frontMatter.tags, blocks, secrets: secrets.filter(secret => Boolean(secret.value)) };
}

export function downloadMarkdownTemplate() {
  const blob = new Blob([markdownImportTemplate], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = 'ourora-notes-template.md';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
